'use client';

import { type ModalProps } from '@heroui/react';
import React from 'react';
import { Modal } from '@/components/ui';
import { cn } from '@/utils';
import { useScrollFocusedInputIntoView } from '@/utils/use-scroll-focused-input-into-view';
import { useVisualViewportVar } from '@/utils/use-visual-viewport-var';
import ModalReviewContent, { type EditTarget } from './modal-review-content';

type ModalReviewProps = Omit<ModalProps, 'children'> & {
  productId: string;
  editTarget?: EditTarget;
};

/** Mount only while open so viewport/scroll helpers don't leak to the page behind. */
const ModalReviewMobileSupport = () => {
  // iOS Safari/Chrome: `dvh` does not shrink when the soft keyboard opens.
  useVisualViewportVar();
  // iOS does not auto-scroll focused fields inside a modal scroll container.
  useScrollFocusedInputIntoView();
  return null;
};

const ModalReview = React.forwardRef<HTMLDivElement, ModalReviewProps>(
  ({ isOpen, onOpenChange, productId, editTarget, classNames, ...props }, ref) => {
    return (
      <Modal
        isOpen={isOpen}
        scrollBehavior="inside"
        size="md"
        onOpenChange={onOpenChange}
        classNames={{
          ...classNames,
          base: cn('w-full max-h-[calc(var(--app-vvh,100dvh)-1rem)]', classNames?.base),
        }}
        {...props}
        ref={ref}
      >
        {isOpen ? (
          <>
            <ModalReviewMobileSupport />
            <ModalReviewContent
              key={editTarget?.id ?? 'new'}
              editTarget={editTarget}
              productId={productId}
              onClose={() => onOpenChange?.(false)}
            />
          </>
        ) : null}
      </Modal>
    );
  },
);

ModalReview.displayName = 'ModalReview';

export default ModalReview;
